<template>
  <Layout>
    <div class="kanban-view" data-component="KanbanVisites">

      <!-- Loading state -->
      <div v-if="loading" class="kanban-view__loading">
        <i class="fas fa-circle-notch fa-spin"></i>
        <span>Chargement du kanban…</span>
      </div>

      <template v-else>
        <KanbanHeader
          :stats="boardStats"
          :filter-options="filterOptions"
          @filters-changed="onFiltersChanged"
        />
        <KanbanBoard
          :columns="columns"
          @card-click="openModal"
        />
      </template>

      <!-- Transition modal -->
      <KanbanTransitionModal
        v-if="selectedCard"
        :visible="modalVisible"
        :visit="selectedCard"
        :users="users"
        @close="closeModal"
        @transition-confirmed="onTransitionConfirmed"
      />

    </div>
  </Layout>
</template>

<script>
import AppLayout from '../components/Layout/AppLayout.vue'
import { KanbanHeader, KanbanBoard, KanbanTransitionModal } from '../components/Kanban'

// ── Column definitions ──────────────────────────────────────────────────────
const COLUMN_DEFINITIONS = [
  {
    id: 'a-programmer',
    label: 'À programmer',
    color: '#F39178',
    colorLight: 'rgba(243, 145, 120, 0.1)',
    icon: 'fas fa-clock'
  },
  {
    id: 'a-attribuer',
    label: 'À attribuer',
    color: '#6E88FF',
    colorLight: 'rgba(110, 136, 255, 0.1)',
    icon: 'fas fa-user-plus'
  },
  {
    id: 'en-cours',
    label: 'En cours',
    color: '#00CFB1',
    colorLight: 'rgba(0, 207, 177, 0.1)',
    icon: 'fas fa-bolt'
  },
  {
    id: 'pre-chiffrage',
    label: 'Pré-chiffrage',
    color: '#68CFDF',
    colorLight: 'rgba(104, 207, 223, 0.1)',
    icon: 'fas fa-calculator'
  },
  {
    id: 'termine',
    label: 'Terminé',
    color: '#61BC80',
    colorLight: 'rgba(97, 188, 128, 0.1)',
    icon: 'fas fa-check-circle'
  }
]

/**
 * Détermine la colonne Kanban d'une propriété en fonction de son état.
 */
function resolveColumnId(property) {
  if (!property.scheduledVisitStatus) return null
  const hasManager   = !!property.technicalManager?.id
  const hasPlanified = !!property.planifiedAt
  const hasVisits    = Array.isArray(property.visits)    && property.visits.length > 0
  const hasPreChiff  = Array.isArray(property.preChiffrages) && property.preChiffrages.length > 0
  if (hasPreChiff)                return 'termine'
  if (hasVisits)                  return 'pre-chiffrage'
  if (hasManager && hasPlanified) return 'en-cours'
  if (hasPlanified)               return 'a-attribuer'
  return 'a-programmer'
}

/**
 * Transforme une propriété Dexie en objet carte Kanban.
 */
function propertyToCard(property, columnId) {
  const address = property.address || {}
  const manager = property.technicalManager
  const lastVisit = Array.isArray(property.visits) && property.visits.length > 0
    ? property.visits[property.visits.length - 1]
    : null
  return {
    id:               property.id,
    columnId,
    nsil:             property.nsil || property.propertyCode || `Lot ${property.id}`,
    propertyType:     property.type || '',
    occupancy:        property.occupancy || '',
    address: {
      zipCode: address.zipCode || '',
      city:    address.city    || '',
      street:  address.street  || ''
    },
    mandant:          property.assistance?.mandator?.name || null,
    mandantId:        property.assistance?.mandator?.id   || null,
    technicalManager: manager
      ? (manager.fullName || `${manager.firstName || ''} ${manager.lastName || ''}`.trim())
      : null,
    technicalManagerId: manager?.id || null,
    scheduledDate:    property.planifiedAt      || null,
    lastVisitDate:    lastVisit?.date           || lastVisit?.scheduledAt || null,
    scheduledVisitAt: property.scheduledVisitAt || null
  }
}

/**
 * Filtre les cartes selon les critères actifs.
 */
function applyFilters(cards, { search, mandant, managerId, occupancy }) {
  const term = search?.toLowerCase().trim()
  return cards.filter(card => {
    // Recherche textuelle (NSIL, ville, rue, CP)
    if (term) {
      const haystack = [card.nsil, card.address.city, card.address.street, card.address.zipCode]
        .filter(Boolean).join(' ').toLowerCase()
      if (!haystack.includes(term)) return false
    }
    // Mandant
    if (mandant && card.mandant !== mandant) return false
    // Technicien
    if (managerId && card.technicalManagerId !== managerId) return false
    // Occupation
    if (occupancy && card.occupancy !== occupancy) return false
    return true
  })
}

export default {
  name: 'KanbanVisitesView',
  components: {
    Layout: AppLayout,
    KanbanHeader,
    KanbanBoard,
    KanbanTransitionModal
  },
  data() {
    return {
      loading:       true,
      allVisits:     [],   // toutes les cartes brutes (non filtrées)
      users:         [],   // techniciens pour la modale
      selectedCard:  null,
      modalVisible:  false,
      // Filtres actifs
      activeFilters: {
        search:    '',
        mandant:   '',
        managerId: '',
        occupancy: ''
      }
    }
  },
  computed: {
    // Cartes après application des filtres
    filteredVisits() {
      return applyFilters(this.allVisits, this.activeFilters)
    },

    // Options de filtre calculées depuis les données brutes
    filterOptions() {
      // Mandants uniques (triés)
      const mandants = [...new Set(
        this.allVisits.map(v => v.mandant).filter(Boolean)
      )].sort()

      // Techniciens uniques (triés par nom)
      const managerMap = {}
      this.allVisits.forEach(v => {
        if (v.technicalManagerId && v.technicalManager) {
          managerMap[v.technicalManagerId] = v.technicalManager
        }
      })
      const managers = Object.entries(managerMap)
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))

      return { mandants, managers }
    },

    // Colonnes avec cartes filtrées
    columns() {
      return COLUMN_DEFINITIONS.map(def => ({
        ...def,
        cards: this.filteredVisits.filter(v => v.columnId === def.id)
      }))
    },

    // Stats pour le header (basées sur les filtrés)
    boardStats() {
      return {
        total: this.filteredVisits.length,
        columns: this.columns.map(col => ({
          id:    col.id,
          label: col.label,
          icon:  col.icon,
          color: col.color,
          count: col.cards.length
        }))
      }
    }
  },
  async mounted() {
    // ── Plein écran : bloque le scroll de page, layout flex ───────────────
    document.body.classList.add('is-kanban-page')
    const navbar = document.querySelector('.layout-header')
    if (navbar) {
      document.documentElement.style.setProperty('--navbar-h', navbar.offsetHeight + 'px')
    }

    await Promise.all([this.loadVisits(), this.loadUsers()])
    this.loading = false
  },
  beforeUnmount() {
    document.body.classList.remove('is-kanban-page')
    document.documentElement.style.removeProperty('--navbar-h')
  },
  methods: {
    // ── Chargement des propriétés visitables depuis Dexie ─────────────────
    async loadVisits() {
      try {
        const dexieService = (await import('../services/dexieService')).default
        await dexieService.init()
        const allProperties = await dexieService.getAllProperties()
        const cards = []
        for (const prop of allProperties) {
          const columnId = resolveColumnId(prop)
          if (columnId) cards.push(propertyToCard(prop, columnId))
        }
        this.allVisits = cards
      } catch (error) {
        console.error('[KanbanVisites] Erreur chargement propriétés:', error)
        this.allVisits = []
      }
    },

    // ── Chargement des utilisateurs pour la modale ────────────────────────
    async loadUsers() {
      try {
        const dexieService = (await import('../services/dexieService')).default
        const allUsers = await dexieService.getUsers()
        this.users = allUsers.filter(u => u.enabled !== false)
      } catch (error) {
        console.error('[KanbanVisites] Erreur chargement utilisateurs:', error)
        this.users = []
      }
    },

    // ── Filtres ──────────────────────────────────────────────────────────
    onFiltersChanged(filters) {
      this.activeFilters = { ...filters }
    },

    // ── Modal ─────────────────────────────────────────────────────────────
    openModal(visit) {
      this.selectedCard = visit
      this.modalVisible = true
    },
    closeModal() {
      this.modalVisible = false
      setTimeout(() => { this.selectedCard = null }, 250)
    },
    onTransitionConfirmed(payload) {
      console.info('[KanbanVisites] Transition confirmée (présentation) :', payload)
      // TODO: appeler mutationService selon payload.columnId, puis:
      //   await this.loadVisits()
      this.closeModal()
    }
  }
}
</script>

<!-- ── Styles globaux (non-scoped) actifs seulement quand is-kanban-page ── -->
<style lang="scss">
// Bloque le scroll de la page entière
body.is-kanban-page {
  overflow: hidden;

  // Le wrapper Layout devient flex column pleine hauteur
  [data-component="Layout"] {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  // La navbar ne se réduit pas
  .layout-header {
    flex-shrink: 0;
  }

  // La vue kanban remplit tout l'espace restant
  .kanban-view {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}
</style>

<!-- ── Styles scoped de la vue ─────────────────────────────────────────── -->
<style lang="scss" scoped>
@use './../assets/styles/variables' as *;

.kanban-view {
  width: 100%;
  box-sizing: border-box;
  padding: 1.5rem 1.75rem;

  @media (max-width: 1280px) { padding: 1.25rem; }
  @media (max-width: 1024px) { padding: 1rem; }
  @media (max-width: 768px)  { padding: 0.75rem; }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    flex: 1;
    color: $bluegray;
    font-size: $title6;

    i { color: $lightblue; font-size: $title4; }
  }
}
</style>
